import { useState } from 'react';
import { qs } from '../lib/api.js';
import { useApi, useDebounced } from '../lib/hooks.js';
import { ErrorNote, Spinner } from './ui.jsx';

/**
 * Pick a few things you already have, to cook around.
 *
 * No "create it" escape hatch, unlike ItemPicker: this list is the answer to
 * "what can I make tonight", and you cannot cook with an item that does not
 * exist yet. Inventing one here would only produce a search for a dish built
 * around something that is not in the kitchen.
 *
 * Capped at three on purpose. Every id past the third narrows the recipe search
 * towards nothing, and the pantry check that runs afterwards already accounts
 * for everything else on the shelves -- so a longer list buys no accuracy and
 * costs results.
 */
export default function OnHandPicker({ selected, onChange, max = 3 }) {
  const [query, setQuery] = useState('');
  // Local and free, unlike the web search this feeds: typing here only ever
  // hits our own items table, so it can search as you type.
  const debounced = useDebounced(query, 250);
  const expiring = useApi('/inventory/expiring?days=7');

  const atMax = selected.length >= max;
  const term = debounced.trim();
  const searching = Boolean(term) && !atMax;
  const results = useApi(
    searching ? `/items${qs({ q: term, limit: 25, inStock: true })}` : null,
    { skip: !searching },
  );

  const chosen = new Set(selected.map((item) => item.id));

  // One pass does both jobs: the same master item can be expiring in three
  // different tubs, and anything already picked is not a suggestion.
  const suggestions = [];
  for (const row of expiring.data?.items || []) {
    if (!row.master_item_id || chosen.has(row.master_item_id)) continue;
    chosen.add(row.master_item_id);
    suggestions.push({ id: row.master_item_id, name: row.item_name });
    if (suggestions.length === 6) break;
  }

  // useApi keeps the previous path's data, so an emptied box would otherwise
  // leave the last search's rows sitting there as if they were still results.
  const matches = searching && !results.loading && !results.error
    ? (results.data?.items || []).filter((item) => !selected.some((s) => s.id === item.id))
    : [];

  const add = (item) => {
    if (atMax || selected.some((s) => s.id === item.id)) return;
    onChange([...selected, { id: item.id, name: item.name }]);
    setQuery('');
  };

  return (
    <div className="stack">
      {selected.length > 0 && (
        <div className="chips">
          {selected.map((item) => (
            <button
              key={item.id}
              type="button"
              className="chip active"
              onClick={() => onChange(selected.filter((s) => s.id !== item.id))}
              aria-label={`Remove ${item.name}`}
            >
              {item.name} ✕
            </button>
          ))}
        </div>
      )}

      {!atMax && suggestions.length > 0 && (
        <div>
          <div className="tiny muted">Use these up first</div>
          <div className="chips" style={{ marginTop: 6 }}>
            {suggestions.map((item) => (
              <button key={item.id} type="button" className="chip" onClick={() => add(item)}>
                {item.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <input
        type="search"
        value={query}
        disabled={atMax}
        placeholder={atMax
          ? `${max} is plenty — the pantry check covers the rest.`
          : 'Search what you have…'}
        onChange={(event) => setQuery(event.target.value)}
        aria-label="Search items you have on hand"
      />

      {searching && results.loading && (
        <div className="small muted"><Spinner /> searching…</div>
      )}

      {/* A failed lookup must not impersonate "you have nothing like that". */}
      {searching && <ErrorNote error={results.error} />}

      {matches.length > 0 && (
        <div className="card list" style={{ padding: '4px 12px' }}>
          {matches.map((item) => (
            <button
              key={item.id}
              type="button"
              className="ghost"
              style={{ justifyContent: 'flex-start', width: '100%', textAlign: 'left' }}
              onClick={() => add(item)}
            >
              <span className="grow truncate">
                {item.name}
                {item.brand && <span className="muted small"> · {item.brand}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
